import { Download as DownloadIcon } from '@mui/icons-material';
import { Box, IconButton, Link as MuiLink, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import React, { useMemo } from 'react';
import GridData from 'src/components/GridData/GridData';
import { getDateTimeUSAFormatWithTime } from 'src/constants/helpers';
import { downloadInvalidContactsCsv } from '../../helpers/downloadInvalidContactsCsv';
import { ContactImportLogRow } from '../../helpers/importLogs';
import { useContactImportLogsViewController } from '../../controllers';

export const ContactImportLogsView = () => {
  const { isLoading, rows, count, pagination, changePagination, downloadImportFile, downloadingFileId } = useContactImportLogsViewController();

  const columns: GridColDef<ContactImportLogRow>[] = useMemo(
    () => [
      {
        field: 'file',
        headerName: 'File name',
        minWidth: 200,
        editable: false,
        sortable: false,
        flex: 1,
        renderCell: params => {
          const { file } = params.row;

          if (!file?.name) {
            return '-';
          }

          if (!file.url) {
            return file.name;
          }

          return (
            <MuiLink
              component="button"
              type="button"
              disabled={downloadingFileId === file.id}
              onClick={event => {
                event.stopPropagation();
                downloadImportFile(file.id);
              }}
            >
              <Typography variant="caption" color="primary">
                {file.name}
              </Typography>
            </MuiLink>
          );
        },
      },
      {
        field: 'dateUpdated',
        headerName: 'Date',
        minWidth: 180,
        editable: false,
        sortable: false,
        flex: 1,
        renderCell: params => {
          if (!params.row.dateUpdated) {
            return '-';
          }

          return getDateTimeUSAFormatWithTime(new Date(params.row.dateUpdated));
        },
      },
      {
        field: 'imported',
        headerName: 'Imported',
        type: 'number',
        minWidth: 140,
        editable: false,
        sortable: false,
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => (value ?? 0).toLocaleString('en-US'),
      },
      {
        field: 'updated',
        headerName: 'Updated',
        type: 'number',
        minWidth: 140,
        editable: false,
        sortable: false,
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => (value ?? 0).toLocaleString('en-US'),
      },
      {
        field: 'rejected',
        headerName: 'Rejected / With error(s)',
        minWidth: 200,
        editable: false,
        sortable: false,
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        renderCell: params => {
          const { rejected, withErrors } = params.row;
          return `${(rejected ?? 0).toLocaleString('en-US')} / ${(withErrors ?? 0).toLocaleString('en-US')}`;
        },
      },
    ],
    [downloadImportFile, downloadingFileId],
  );

  return (
    <Box sx={{ height: 'calc(100vh - 200px)' }}>
      <GridData
        isLoading={isLoading}
        rows={rows}
        columns={columns}
        renderAction={(row: ContactImportLogRow) => {
          if (!row.invalidContacts.length) {
            return null;
          }

          return (
            <IconButton
              size="small"
              color="primary"
              aria-label="Download invalid contacts"
              onClick={event => {
                event.stopPropagation();
                downloadInvalidContactsCsv(row.invalidContacts, row.file?.name, row.selectedColumns);
              }}
            >
              <DownloadIcon fontSize="inherit" />
            </IconButton>
          );
        }}
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalCount: count,
          onChange: changePagination,
        }}
      />
    </Box>
  );
};
